import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { DragIndicator } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  fetchEvohomeLocations,
  fetchEvohomeLocationOverview,
  fetchEvohomeLocationSchedules,
  patchEvohomeAiManagement,
  patchEvohomeHeatSupply,
  reorderEvohomeHorario,
  setEvohomeSystemMode,
  type EvohomeDevice,
  type EvohomeLocation,
  type EvohomeScheduleDay,
  type EvohomeSystem,
} from '../../services/evohomeApi';
import api from '../../services/api';
import EvohomeScheduleEditor from './EvohomeScheduleEditor';
import { EVOHOME_CMD_COOLDOWN_MS } from './evohomeCmdGuard';
import { evohomeTempColor } from './evohomeTempColor';
import { isHeatingOffMode } from './evohomeModeUtils';
import { useOrientation } from '../../hooks/useOrientation';

const ACCENT = '#2391FF';
const HEAT = '#FF8A65';
const OFF = '#EF5350';
const CARD_BG = '#2F3235';
const PRESS_TRANSITION =
  'transform 140ms cubic-bezier(0.2, 0.9, 0.3, 1), background-color 160ms ease, border-color 160ms ease, box-shadow 180ms ease';

/** Título fijo; cuerpo flexible según cantidad de periodos (1–6). */
const SCHEDULE_CARD_TITLE_PX = 34;
const SCHEDULE_CARD_DAY_H = 18;
const SCHEDULE_CARD_PERIOD_H = 46;
const SCHEDULE_CARD_BODY_PAD = 8;

const DAYS = [
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
] as const;

type ScheduleCacheEntry = EvohomeScheduleDay[] | 'loading' | 'error' | undefined;

type SchedulePeriodCell = {
  start: string;
  end: string;
  temp: number;
};

function dayKeyToday(): string {
  // JS: 0=Sun … 6=Sat → map to monday-first
  const js = new Date().getDay();
  return DAYS[(js + 6) % 7];
}

function formatTodHHMM(tod: string): string {
  const s = String(tod || '');
  return s.length >= 5 ? s.slice(0, 5) : (s || '00:00');
}

/** Matriz semana: columnas = días; filas = n-ésimo periodo (máx. 6). Fin = inicio del siguiente o 24:00. */
function weekPeriodMatrix(days: EvohomeScheduleDay[]): {
  cols: (SchedulePeriodCell | null)[][];
  rowCount: number;
} {
  const cols = DAYS.map((d) => {
    const day = days.find((x) => x.day_of_week === d);
    const sps = [...(day?.switchpoints || [])].sort((a, b) =>
      String(a.time_of_day || '').localeCompare(String(b.time_of_day || '')),
    );
    return sps.map((sp, idx) => {
      const t = Number(sp.heat_setpoint);
      if (!Number.isFinite(t)) return null;
      const start = formatTodHHMM(sp.time_of_day);
      const end = idx + 1 < sps.length
        ? formatTodHHMM(sps[idx + 1].time_of_day)
        : '24:00';
      return { start, end, temp: t };
    });
  });
  const rowCount = Math.min(
    6,
    cols.reduce((m, c) => Math.max(m, c.length), 0),
  );
  return { cols, rowCount };
}

function formatTempCell(temp: number): string {
  const rounded = Math.round(temp * 2) / 2;
  const text = Number.isInteger(rounded) || Math.abs(rounded % 1) < 0.05
    ? String(Math.round(rounded))
    : rounded.toFixed(1);
  return `${text}°`;
}

/** Claves i18n de chips / ayuda (settings.evohomeDiscovery.mode.* / systemHelp.*). */
const SYSTEM_MODE_HELP_KEYS = [
  'auto',
  'eco',
  'reset',
  'away',
  'dayOff',
  'heatingOff',
  'custom',
] as const;

/** Canonical snake_case for SystemMode values from TCC / DB. */
export function normalizeSystemMode(mode: string | null | undefined): string {
  return String(mode || '')
    .trim()
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .replace(/-/g, '_')
    .toLowerCase();
}

function modeLabelKey(mode: string): string {
  const n = normalizeSystemMode(mode);
  const keys: Record<string, string> = {
    auto: 'auto',
    auto_with_eco: 'eco',
    auto_with_reset: 'reset',
    away: 'away',
    day_off: 'dayOff',
    heating_off: 'heatingOff',
    off: 'heatingOff',
    custom: 'custom',
  };
  return keys[n] || n;
}

function SortableScheduleZoneCard({
  zone,
  schedule,
  onOpen,
}: {
  zone: EvohomeDevice;
  schedule: ScheduleCacheEntry;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: zone.id });

  const todayKey = dayKeyToday();

  const matrix = useMemo(() => {
    if (!Array.isArray(schedule)) return null;
    return weekPeriodMatrix(schedule);
  }, [schedule]);

  const dayLetters = useMemo(
    () => DAYS.map((d) => t(`climateSystem.schedules.daysLetter.${d}`, {
      defaultValue: d.slice(0, 1).toUpperCase(),
    })),
    [t],
  );

  const periodRows = matrix?.rowCount || 0;
  const bodyH = schedule === 'loading' || schedule === undefined || schedule === 'error' || !periodRows
    ? 72
    : SCHEDULE_CARD_BODY_PAD + SCHEDULE_CARD_DAY_H + periodRows * SCHEDULE_CARD_PERIOD_H
      + Math.max(0, periodRows - 1) * 2;
  const cardH = SCHEDULE_CARD_TITLE_PX + bodyH;

  return (
    <Box
      ref={setNodeRef}
      sx={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.88 : 1,
        zIndex: isDragging ? 5 : 1,
        width: '100%',
        height: cardH,
        minHeight: cardH,
        maxHeight: cardH,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: '12px',
        bgcolor: CARD_BG,
        border: `1px solid ${isDragging ? `${HEAT}66` : 'rgba(255,255,255,0.07)'}`,
        boxShadow: isDragging ? `0 8px 22px ${HEAT}33` : 'none',
      }}
    >
      {/* Título */}
      <Box
        sx={{
          height: SCHEDULE_CARD_TITLE_PX,
          minHeight: SCHEDULE_CARD_TITLE_PX,
          maxHeight: SCHEDULE_CARD_TITLE_PX,
          px: 0.5,
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
          minWidth: 0,
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          bgcolor: 'rgba(0,0,0,0.22)',
          boxSizing: 'border-box',
        }}
      >
        <Box
          {...attributes}
          {...listeners}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            width: 28,
            height: 28,
            color: '#bbb',
            cursor: 'grab',
            touchAction: 'none',
            '&:active': { cursor: 'grabbing' },
          }}
          aria-label="reorder"
        >
          <DragIndicator sx={{ fontSize: 18 }} />
        </Box>
        <Typography
          onClick={onOpen}
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: 13,
            fontWeight: 700,
            color: '#fff',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            cursor: 'pointer',
            lineHeight: 1.2,
          }}
        >
          {zone.nombre}
        </Typography>
      </Box>

      {/* Cuerpo — tabla 7 días × N periodos (hora inicio→fin + temp) */}
      <Box
        component="button"
        type="button"
        onClick={onOpen}
        sx={{
          appearance: 'none',
          border: 'none',
          bgcolor: 'transparent',
          color: '#fff',
          height: bodyH,
          minHeight: bodyH,
          maxHeight: bodyH,
          px: 0.35,
          py: 0.35,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          justifyContent: 'flex-start',
          cursor: 'pointer',
          transition: PRESS_TRANSITION,
          WebkitTapHighlightColor: 'transparent',
          textAlign: 'left',
          overflow: 'hidden',
          boxSizing: 'border-box',
          '&:hover': { bgcolor: `${HEAT}10` },
          '&:active': { transform: 'scale(0.985)' },
        }}
      >
        {schedule === 'loading' || schedule === undefined ? (
          <Box sx={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          >
            <CircularProgress size={18} sx={{ color: HEAT }} />
          </Box>
        ) : schedule === 'error' ? (
          <Typography sx={{ fontSize: 11, color: '#e57373', mt: 0.5, px: 0.4 }}>
            {t('climateSystem.schedules.cardLoadError')}
          </Typography>
        ) : !matrix || matrix.rowCount === 0 ? (
          <Typography sx={{ fontSize: 11, color: '#888', mt: 0.5, px: 0.4 }}>
            {t('climateSystem.schedules.cardEmpty')}
          </Typography>
        ) : (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'grid',
              gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
              gridTemplateRows: periodRows
                ? `${SCHEDULE_CARD_DAY_H}px repeat(${periodRows}, ${SCHEDULE_CARD_PERIOD_H}px)`
                : `${SCHEDULE_CARD_DAY_H}px`,
              columnGap: '2px',
              rowGap: '2px',
              width: '100%',
            }}
          >
            {DAYS.map((d, col) => {
              const isToday = d === todayKey;
              return (
                <Box
                  key={`h-${d}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: SCHEDULE_CARD_DAY_H,
                    borderRadius: '3px',
                    bgcolor: isToday ? 'rgba(255,138,101,0.22)' : 'transparent',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 800,
                      lineHeight: 1,
                      color: isToday ? HEAT : '#ccc',
                    }}
                  >
                    {dayLetters[col]}
                  </Typography>
                </Box>
              );
            })}
            {Array.from({ length: periodRows }, (_, row) =>
              DAYS.map((d, col) => {
                const cell = matrix.cols[col][row] ?? null;
                const has = cell != null;
                return (
                  <Box
                    key={`c-${d}-${row}`}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '1px',
                      minWidth: 0,
                      height: SCHEDULE_CARD_PERIOD_H,
                      borderRadius: '4px',
                      bgcolor: has
                        ? (d === todayKey ? 'rgba(255,138,101,0.08)' : 'rgba(255,255,255,0.03)')
                        : 'transparent',
                      px: 0.1,
                      boxSizing: 'border-box',
                    }}
                  >
                    {has ? (
                      <>
                        <Typography
                          sx={{
                            fontSize: 9.5,
                            fontWeight: 600,
                            lineHeight: 1.05,
                            color: '#ddd',
                            fontVariantNumeric: 'tabular-nums',
                            whiteSpace: 'nowrap',
                            letterSpacing: '-0.02em',
                          }}
                        >
                          {cell!.start}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 9.5,
                            fontWeight: 600,
                            lineHeight: 1.05,
                            color: '#ccc',
                            fontVariantNumeric: 'tabular-nums',
                            whiteSpace: 'nowrap',
                            letterSpacing: '-0.02em',
                          }}
                        >
                          →{cell!.end}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontWeight: 800,
                            lineHeight: 1.05,
                            color: evohomeTempColor(cell!.temp),
                            fontVariantNumeric: 'tabular-nums',
                            whiteSpace: 'nowrap',
                            mt: '1px',
                          }}
                        >
                          {formatTempCell(cell!.temp)}
                        </Typography>
                      </>
                    ) : null}
                  </Box>
                );
              }),
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export function extractAllowedModes(system: EvohomeSystem | undefined): string[] {
  const raw = system?.allowed_system_modes;
  const fallback = ['auto', 'auto_with_eco', 'away', 'day_off', 'heating_off', 'custom'];
  if (!Array.isArray(raw) || raw.length === 0) return fallback;
  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    const rawMode = typeof item === 'string'
      ? item
      : (item.systemMode || item.mode || '');
    const n = normalizeSystemMode(rawMode);
    if (!n || seen.has(n)) continue;
    seen.add(n);
    out.push(n);
  }
  return out.length ? out : fallback;
}

export { isHeatingOffMode };

const HEAT_OUTLET_TYPES = new Set([
  'PLUS1PM',
  '1PMG3',
  'PRO1PM',
  'EM',
  'EM3',
  'PROEM50',
  'PRO3EM63',
]);

type HeatOutletOption = { id: number | null; nombre: string };

function numOr(v: unknown, fallback: number): number {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function HeatSupplySettings({
  location,
  isDemoUser,
  onDemoActionAttempt,
  onSaved,
}: {
  location: EvohomeLocation;
  isDemoUser?: boolean;
  onDemoActionAttempt?: () => void;
  onSaved: (row: EvohomeLocation) => void;
}) {
  const { t } = useTranslation();
  const marginRef = React.useRef<HTMLInputElement>(null);
  const minOnRef = React.useRef<HTMLInputElement>(null);
  const savedTimer = React.useRef<number | null>(null);
  const [outlets, setOutlets] = useState<{ id: number; nombre: string }[]>([]);
  const [outletsLoading, setOutletsLoading] = useState(true);
  const [deviceId, setDeviceId] = useState<number | null>(location.heat_supply_device_id ?? null);
  const [marginText, setMarginText] = useState(() => String(numOr(location.frost_margin_c, 2)));
  const [minOnText, setMinOnText] = useState(() => String(numOr(location.frost_min_on_minutes, 10)));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [heatError, setHeatError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setOutletsLoading(true);
      try {
        const res = await api.get('/dispositivos');
        const raw = Array.isArray(res.data) ? res.data : [];
        const next = raw
          .filter((d: { tipo?: string }) => HEAT_OUTLET_TYPES.has(String(d?.tipo || '').trim().toUpperCase()))
          .map((d: { id?: number; nombre?: string }) => ({
            id: Number(d.id),
            nombre: String(d.nombre || '').trim() || String(d.id),
          }))
          .filter((d: { id: number }) => Number.isFinite(d.id));
        next.sort((a: { nombre: string }, b: { nombre: string }) => a.nombre.localeCompare(b.nombre, undefined, { sensitivity: 'base' }));
        if (!cancelled) setOutlets(next);
      } catch {
        if (!cancelled) setOutlets([]);
      } finally {
        if (!cancelled) setOutletsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (dirty) return;
    setDeviceId(location.heat_supply_device_id ?? null);
    setMarginText(String(numOr(location.frost_margin_c, 2)));
    setMinOnText(String(numOr(location.frost_min_on_minutes, 10)));
  }, [dirty, location.heat_supply_device_id, location.frost_margin_c, location.frost_min_on_minutes]);

  useEffect(() => () => {
    if (savedTimer.current != null) window.clearTimeout(savedTimer.current);
  }, []);

  const noneLabel = t('settings.evohomeDiscovery.heatSupply.outletNone');
  const options = useMemo(() => {
    const rows: HeatOutletOption[] = [
      { id: null, nombre: noneLabel },
      ...outlets.map((o) => ({ id: o.id, nombre: o.nombre })),
    ];
    if (deviceId != null && !rows.some((o) => o.id === deviceId)) {
      rows.push({ id: deviceId, nombre: `#${deviceId}` });
    }
    return rows;
  }, [outlets, deviceId, noneLabel]);
  const value: HeatOutletOption = options.find((o) => o.id === deviceId) ?? options[0];

  const mark = () => {
    setDirty(true);
    setSaved(false);
  };

  const handleSave = async () => {
    if (isDemoUser) {
      onDemoActionAttempt?.();
      return;
    }
    if (marginRef.current && !marginRef.current.checkValidity()) {
      marginRef.current.reportValidity();
      return;
    }
    if (minOnRef.current && !minOnRef.current.checkValidity()) {
      minOnRef.current.reportValidity();
      return;
    }
    const margin = Number(String(marginText).trim().replace(',', '.'));
    const minOn = Number(String(minOnText).trim().replace(',', '.'));
    if (!Number.isFinite(margin) || margin < 0.5 || margin > 30) return;
    if (!Number.isInteger(minOn) || minOn < 2) return;
    setSaving(true);
    setHeatError(null);
    try {
      const row = await patchEvohomeHeatSupply(location.id, {
        device_id: deviceId,
        frost_margin_c: margin,
        frost_min_on_minutes: minOn,
      });
      setDirty(false);
      setMarginText(String(margin));
      setMinOnText(String(minOn));
      setSaved(true);
      if (savedTimer.current != null) window.clearTimeout(savedTimer.current);
      savedTimer.current = window.setTimeout(() => setSaved(false), 2500);
      onSaved(row);
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === 'string' ? d : (d?.message ? String(d.message) : '');
      setHeatError(msg || null);
    } finally {
      setSaving(false);
    }
  };

  const fieldSx = {
    '& .MuiInputBase-root': {
      color: '#bbb',
      bgcolor: 'rgba(0,0,0,0.22)',
      fontSize: 14,
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#444' },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: ACCENT },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ACCENT },
    '& input': { color: '#bbb' },
  };
  const titleSx = { fontSize: 13, fontWeight: 700, color: '#bbb', lineHeight: 1.3 };
  const hintSx = { fontSize: 11, color: '#bbb', lineHeight: 1.35, mt: 0.25, mb: 0.75 };

  return (
    <Box
      sx={{
        mb: 1.5,
        mt: 0.25,
        p: 1.25,
        borderRadius: '12px',
        bgcolor: CARD_BG,
        border: '1px solid rgba(255,255,255,0.07)',
      }}
    >
      <Typography sx={titleSx}>{t('settings.evohomeDiscovery.heatSupply.outletTitle')}</Typography>
      <Typography sx={hintSx}>{t('settings.evohomeDiscovery.heatSupply.outletHint')}</Typography>
      <Autocomplete
        options={options}
        value={value}
        onChange={(_, v) => {
          mark();
          setDeviceId(v ? v.id : null);
        }}
        getOptionLabel={(o) => o.nombre}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        disableClearable
        loading={outletsLoading}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={t('settings.evohomeDiscovery.heatSupply.outletSearch')}
            size="small"
            sx={fieldSx}
          />
        )}
        sx={{
          mb: 1.25,
          '& .MuiAutocomplete-popupIndicator': { color: '#bbb' },
          '& .MuiAutocomplete-clearIndicator': { color: '#bbb' },
        }}
        componentsProps={{
          paper: {
            sx: {
              backgroundColor: '#1E1E1E',
              color: '#bbb',
              border: '1px solid #444',
              '& .MuiAutocomplete-option': {
                fontSize: '0.85rem',
                color: '#bbb',
                '&[aria-selected="true"]': { backgroundColor: 'rgba(35,145,255,0.25)' },
                '&.Mui-focused': { backgroundColor: 'rgba(35,145,255,0.15)' },
              },
            },
          },
        }}
      />

      <Typography sx={titleSx}>{t('settings.evohomeDiscovery.heatSupply.marginTitle')}</Typography>
      <Typography sx={hintSx}>{t('settings.evohomeDiscovery.heatSupply.marginHint')}</Typography>
      <TextField
        value={marginText}
        onChange={(e) => { mark(); setMarginText(e.target.value); }}
        type="number"
        size="small"
        required
        inputRef={marginRef}
        inputProps={{ min: 0.5, max: 30, step: 0.5 }}
        sx={{ ...fieldSx, width: 120, mb: 1.25 }}
      />

      <Typography sx={titleSx}>{t('settings.evohomeDiscovery.heatSupply.minOnTitle')}</Typography>
      <Typography sx={hintSx}>{t('settings.evohomeDiscovery.heatSupply.minOnHint')}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
        <TextField
          value={minOnText}
          onChange={(e) => { mark(); setMinOnText(e.target.value); }}
          type="number"
          size="small"
          required
          inputRef={minOnRef}
          inputProps={{ min: 2, step: 1 }}
          sx={{ ...fieldSx, width: 120 }}
        />
        <Typography sx={{ fontSize: 13, color: '#bbb' }}>
          {t('settings.evohomeDiscovery.heatSupply.minOnUnit')}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Button
          variant="contained"
          disabled={saving}
          onClick={() => { void handleSave(); }}
          sx={{
            bgcolor: ACCENT,
            color: '#fff',
            textTransform: 'none',
            fontWeight: 700,
            '&:hover': { bgcolor: ACCENT },
          }}
        >
          {saving
            ? <CircularProgress size={16} sx={{ color: '#fff' }} />
            : t('settings.evohomeDiscovery.heatSupply.save')}
        </Button>
        {saved && (
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: ACCENT }}>
            {t('settings.evohomeDiscovery.heatSupply.saved')}
          </Typography>
        )}
      </Box>
      {heatError && (
        <Typography sx={{ color: '#e57373', fontSize: 12, mt: 0.75 }}>{heatError}</Typography>
      )}
    </Box>
  );
}

export interface EvohomeSystemControlsProps {
  isDemoUser?: boolean;
  onDemoActionAttempt?: () => void;
  /** Si se pasa, el padre controla la locación seleccionada (opcional). */
  selectedLocationId?: number | null;
  onSelectedLocationIdChange?: (id: number | null) => void;
  /** Callback con overview actual (p. ej. para listar zonas en Discovery). */
  onOverviewChange?: (data: {
    locationId: number | null;
    systems: EvohomeSystem[];
    zones: EvohomeDevice[];
  }) => void;
  /** Intervalo de refresh del overview (ms). Default 45000. */
  refreshIntervalMs?: number;
  /** Ref para back anidado (editor de horario). Devuelve true si consumió el back. */
  nestedBackRef?: React.MutableRefObject<(() => boolean) | null>;
  /** Bloque enchufe/margen/mínimo. Solo Descubrimiento lo pasa en true. */
  showHeatSupplySettings?: boolean;
}

/**
 * Controles generales Evohome: locación + modo de sistema (Auto/Eco/Away/…).
 * Misma UI que Settings → Discovery → Evohome.
 */
const EvohomeSystemControls: React.FC<EvohomeSystemControlsProps> = ({
  isDemoUser,
  onDemoActionAttempt,
  selectedLocationId: controlledLocationId,
  onSelectedLocationIdChange,
  onOverviewChange,
  refreshIntervalMs = 45000,
  nestedBackRef,
  showHeatSupplySettings = false,
}) => {
  const { t } = useTranslation();
  const isHorizontal = useOrientation();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [locations, setLocations] = useState<EvohomeLocation[]>([]);
  const [internalLocationId, setInternalLocationId] = useState<number | null>(null);
  const [systems, setSystems] = useState<EvohomeSystem[]>([]);
  const [zones, setZones] = useState<EvohomeDevice[]>([]);
  const [overviewLocation, setOverviewLocation] = useState<EvohomeLocation | null>(null);
  const [aiSaving, setAiSaving] = useState(false);
  const [scheduleZone, setScheduleZone] = useState<EvohomeDevice | null>(null);
  const [scheduleCache, setScheduleCache] = useState<Record<number, ScheduleCacheEntry>>({});
  const [overviewBusy, setOverviewBusy] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [reorderBusy, setReorderBusy] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
  );

  useEffect(() => {
    if (cooldownUntil <= Date.now()) return undefined;
    const id = window.setInterval(() => setNowMs(Date.now()), 200);
    return () => window.clearInterval(id);
  }, [cooldownUntil]);

  const cmdLocked = busy || nowMs < cooldownUntil;

  const selectedLocationId = controlledLocationId !== undefined
    ? controlledLocationId
    : internalLocationId;

  const setSelectedLocationId = useCallback((id: number | null) => {
    if (controlledLocationId === undefined) setInternalLocationId(id);
    onSelectedLocationIdChange?.(id);
  }, [controlledLocationId, onSelectedLocationIdChange]);

  // Refs para callbacks/estado leídos en efectos sin re-dispararlos.
  const selectedLocationIdRef = React.useRef(selectedLocationId);
  selectedLocationIdRef.current = selectedLocationId;
  const onOverviewChangeRef = React.useRef(onOverviewChange);
  onOverviewChangeRef.current = onOverviewChange;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const locs = await fetchEvohomeLocations().catch(() => []);
        if (cancelled) return;
        setLocations(locs || []);
        if ((locs || []).length > 0) {
          const current = selectedLocationIdRef.current;
          const next = current != null && locs.some((l) => l.id === current)
            ? current
            : locs[0].id;
          setSelectedLocationId(next);
        } else {
          setSelectedLocationId(null);
          setSystems([]);
          setZones([]);
          setOverviewLocation(null);
          onOverviewChangeRef.current?.({ locationId: null, systems: [], zones: [] });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [setSelectedLocationId]);

  useEffect(() => {
    if (selectedLocationId == null) return undefined;
    let cancelled = false;
    const run = async () => {
      setOverviewBusy(true);
      try {
        const ov = await fetchEvohomeLocationOverview(selectedLocationId);
        if (cancelled) return;
        const nextSystems = ov.systems || [];
        const nextZones = ov.zones || [];
        setSystems(nextSystems);
        setZones(nextZones);
        setOverviewLocation(ov.location || null);
        onOverviewChangeRef.current?.({
          locationId: selectedLocationId,
          systems: nextSystems,
          zones: nextZones,
        });
      } catch {
        if (cancelled) return;
        setSystems([]);
        setZones([]);
        setOverviewLocation(null);
        onOverviewChangeRef.current?.({
          locationId: selectedLocationId,
          systems: [],
          zones: [],
        });
      } finally {
        if (!cancelled) setOverviewBusy(false);
      }
    };
    void run();
    const timer = window.setInterval(() => { void run(); }, refreshIntervalMs);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [selectedLocationId, refreshIntervalMs]);

  // Clear schedule editor + cache when leaving location
  useEffect(() => {
    setScheduleZone(null);
    setScheduleCache({});
  }, [selectedLocationId]);

  // Horarios: 1) cache DB instantáneo 2) refresh batch paralelo (gate) de faltantes/stale
  useEffect(() => {
    if (selectedLocationId == null || !zones.length) return undefined;
    let cancelled = false;
    const zoneIds = zones.map((z) => z.id);

    const applyEntries = (
      entries: {
        device_id: number;
        daily_schedules: EvohomeScheduleDay[] | null;
      }[],
      opts?: { markMissingError?: boolean },
    ) => {
      setScheduleCache((prev) => {
        const next = { ...prev };
        for (const e of entries) {
          if (Array.isArray(e.daily_schedules)) {
            next[e.device_id] = e.daily_schedules;
          }
        }
        if (opts?.markMissingError) {
          for (const id of zoneIds) {
            if (!Array.isArray(next[id])) next[id] = 'error';
          }
        }
        return next;
      });
    };

    (async () => {
      // Marcar loading solo donde aún no hay datos
      setScheduleCache((prev) => {
        const next = { ...prev };
        for (const id of zoneIds) {
          if (!Array.isArray(next[id])) next[id] = 'loading';
        }
        return next;
      });

      try {
        const cached = await fetchEvohomeLocationSchedules(selectedLocationId, {
          cacheOnly: true,
        });
        if (cancelled) return;
        applyEntries(cached.schedules || []);

        const needsRefresh = (cached.schedules || []).some(
          (e) => !e.cached || e.stale || !Array.isArray(e.daily_schedules),
        ) || zoneIds.some((id) => !(cached.schedules || []).some(
          (e) => e.device_id === id && Array.isArray(e.daily_schedules),
        ));

        // Siempre refrescar en background si falta algo o hay stale;
        // si todo fresco, no tocar TCC.
        if (!needsRefresh) {
          // Asegurar que todas las zonas visibles tienen array (vacío ok)
          setScheduleCache((prev) => {
            const next = { ...prev };
            for (const id of zoneIds) {
              if (!Array.isArray(next[id])) next[id] = [];
            }
            return next;
          });
          return;
        }

        // Mantener loading en las que aún no tienen cache
        setScheduleCache((prev) => {
          const next = { ...prev };
          for (const id of zoneIds) {
            if (!Array.isArray(next[id])) next[id] = 'loading';
          }
          return next;
        });

        const fresh = await fetchEvohomeLocationSchedules(selectedLocationId, {
          force: false,
        });
        if (cancelled) return;
        applyEntries(fresh.schedules || [], { markMissingError: true });
      } catch {
        if (cancelled) return;
        setScheduleCache((prev) => {
          const next = { ...prev };
          for (const id of zoneIds) {
            if (!Array.isArray(next[id])) next[id] = 'error';
          }
          return next;
        });
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocationId, zones.map((z) => z.id).join(',')]);

  const handleScheduleSaved = useCallback((info: {
    zoneId: number;
    days: EvohomeScheduleDay[];
    copiedToIds?: number[];
  }) => {
    setScheduleCache((prev) => {
      const next = { ...prev, [info.zoneId]: info.days };
      for (const tid of info.copiedToIds || []) {
        next[tid] = info.days.map((d) => ({
          day_of_week: d.day_of_week,
          switchpoints: d.switchpoints.map((s) => ({ ...s })),
        }));
      }
      return next;
    });
  }, []);

  const blockDemo = () => {
    if (!isDemoUser) return false;
    onDemoActionAttempt?.();
    return true;
  };

  const primarySystem = useMemo(() => systems[0] || null, [systems]);
  const allowedModes = useMemo(() => extractAllowedModes(primarySystem || undefined), [primarySystem]);
  const currentMode = normalizeSystemMode(primarySystem?.system_mode);
  const heatingOff = isHeatingOffMode(currentMode);

  const labelForMode = useCallback((mode: string) => {
    const key = modeLabelKey(mode);
    const defaults: Record<string, string> = {
      auto: 'Auto',
      eco: 'Eco',
      reset: 'Reiniciar',
      away: 'Ausente',
      dayOff: 'Día libre',
      heatingOff: 'Apagada',
      custom: 'Personalizado',
    };
    return t(`settings.evohomeDiscovery.mode.${key}`, {
      defaultValue: defaults[key] || mode,
    });
  }, [t]);

  const reloadOverview = useCallback(async () => {
    if (selectedLocationId == null) return;
    setOverviewBusy(true);
    try {
      const ov = await fetchEvohomeLocationOverview(selectedLocationId);
      const nextSystems = ov.systems || [];
      const nextZones = ov.zones || [];
      setSystems(nextSystems);
      setZones(nextZones);
      setOverviewLocation(ov.location || null);
      onOverviewChangeRef.current?.({
        locationId: selectedLocationId,
        systems: nextSystems,
        zones: nextZones,
      });
    } catch {
      setSystems([]);
      setZones([]);
      setOverviewLocation(null);
      onOverviewChangeRef.current?.({
        locationId: selectedLocationId,
        systems: [],
        zones: [],
      });
    } finally {
      setOverviewBusy(false);
    }
  }, [selectedLocationId]);

  const handleSystemMode = async (mode: string) => {
    if (blockDemo()) return;
    if (!primarySystem) return;
    if (cmdLocked) return;
    const normalized = normalizeSystemMode(mode);
    if (normalized && normalized === currentMode) return;
    setBusy(true);
    setCooldownUntil(Date.now() + EVOHOME_CMD_COOLDOWN_MS);
    setNowMs(Date.now());
    setError(null);
    setSystems((prev) => prev.map((s, i) => (
      i === 0 ? { ...s, system_mode: normalized } : s
    )));
    try {
      await setEvohomeSystemMode(primarySystem.id, normalized);
      await reloadOverview();
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      setError(typeof d === 'object' ? (d.message || JSON.stringify(d)) : (d || 'Error modo sistema'));
      await reloadOverview();
    } finally {
      setBusy(false);
    }
  };

  const handleZonesDragEnd = useCallback(async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    if (isDemoUser) {
      onDemoActionAttempt?.();
      return;
    }
    const oldIndex = zones.findIndex((z) => z.id === Number(active.id));
    const newIndex = zones.findIndex((z) => z.id === Number(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(zones, oldIndex, newIndex);
    setZones(next);
    setReorderBusy(true);
    try {
      await reorderEvohomeHorario(next.map((z, orden_horario) => ({ id: z.id, orden_horario })));
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      setError(typeof d === 'object' ? (d.message || JSON.stringify(d)) : (d || 'Error al reordenar'));
      await reloadOverview();
    } finally {
      setReorderBusy(false);
    }
  }, [zones, isDemoUser, onDemoActionAttempt, reloadOverview]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
        <CircularProgress sx={{ color: ACCENT }} />
      </Box>
    );
  }

  if (locations.length === 0) {
    return (
      <Typography sx={{ fontSize: 13, color: '#999', px: 0.5, pt: 1 }}>
        {t('climateSystem.systemControls.empty', {
          defaultValue: 'No hay locaciones Evohome. Conectá y sincronizá la cuenta en Settings → Discovery.',
        })}
      </Typography>
    );
  }

  if (scheduleZone) {
    return (
      <EvohomeScheduleEditor
        zone={scheduleZone}
        siblingZones={zones}
        isDemoUser={isDemoUser}
        onDemoActionAttempt={onDemoActionAttempt}
        onClose={() => setScheduleZone(null)}
        onSaved={handleScheduleSaved}
        nestedBackRef={nestedBackRef}
      />
    );
  }

  return (
    <Box sx={{ color: 'white', px: 0.5 }}>
      <Box sx={{ mb: 1.5 }}>
        <Typography sx={{
          fontSize: 12, fontWeight: 700, color: '#999', mb: 0.75,
          letterSpacing: '0.06em', textTransform: 'uppercase',
        }}>
          {t('settings.evohomeDiscovery.location', { defaultValue: 'Locación' })}
          {overviewBusy ? ' …' : ''}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {locations.map((loc) => {
            const selected = loc.id === selectedLocationId;
            return (
              <Chip
                key={loc.id}
                label={loc.nombre || loc.external_id}
                clickable
                onClick={() => setSelectedLocationId(loc.id)}
                sx={{
                  color: selected ? '#fff' : '#bbb',
                  bgcolor: selected ? 'rgba(35,145,255,0.28)' : '#2F3235',
                  border: selected ? `1px solid ${ACCENT}` : '1px solid #444',
                  fontWeight: selected ? 700 : 500,
                }}
              />
            );
          })}
        </Box>
      </Box>

      {primarySystem && (
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.35,
            mb: 0.75,
            minWidth: 0,
          }}>
            <Typography sx={{
              fontSize: 12, fontWeight: 700, color: '#999',
              letterSpacing: '0.06em', textTransform: 'uppercase',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {t('settings.evohomeDiscovery.system', { defaultValue: 'Sistema' })}
              {currentMode ? ` · ${labelForMode(currentMode)}` : ''}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setHelpOpen(true)}
              aria-label={t('settings.evohomeDiscovery.systemHelp.aria')}
              sx={{
                color: '#999',
                p: 0.25,
                flexShrink: 0,
                '&:hover': {
                  backgroundColor: 'rgba(35, 145, 255, 0.1)',
                  color: ACCENT,
                },
              }}
            >
              <HelpOutlineIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
          {heatingOff && (
            <Box sx={{
              mb: 1,
              px: 1.1,
              py: 0.75,
              borderRadius: 1,
              border: `1px solid ${OFF}`,
              bgcolor: 'rgba(239,83,80,0.12)',
            }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: OFF }}>
                {t('settings.evohomeDiscovery.heatingOffBanner', {
                  defaultValue: 'Calefacción apagada',
                })}
              </Typography>
              <Typography sx={{ fontSize: 11, color: '#ccc', mt: 0.25 }}>
                {t('settings.evohomeDiscovery.heatingOffHint', {
                  defaultValue: 'El sistema no demanda calor. Tocá Auto (u otro modo) para reactivar.',
                })}
              </Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.65 }}>
            {allowedModes.map((mode) => {
              const selected = currentMode === mode;
              const isOff = isHeatingOffMode(mode);
              const accent = isOff ? OFF : HEAT;
              return (
                <Chip
                  key={mode}
                  label={labelForMode(mode)}
                  clickable={!cmdLocked}
                  disabled={cmdLocked}
                  onClick={() => { void handleSystemMode(mode); }}
                  sx={{
                    color: selected ? '#fff' : '#bbb',
                    bgcolor: selected
                      ? (isOff ? 'rgba(239,83,80,0.28)' : 'rgba(255,138,101,0.25)')
                      : '#2F3235',
                    border: selected ? `1px solid ${accent}` : '1px solid #444',
                    fontWeight: selected ? 700 : 500,
                    opacity: cmdLocked && !selected ? 0.55 : 1,
                  }}
                />
              );
            })}
          </Box>
        </Box>
      )}

      {!primarySystem && !overviewBusy && (
        <Typography sx={{ fontSize: 12, color: '#777' }}>
          {t('climateSystem.systemControls.noSystem', {
            defaultValue: 'Sin sistema de calefacción en esta locación.',
          })}
        </Typography>
      )}

      {showHeatSupplySettings && overviewLocation && overviewLocation.id === selectedLocationId && (
        <Box sx={{ mb: 1.5, border: '1px solid #333', borderRadius: 1, p: 1.25, bgcolor: '#1a1a1a' }}>
          <FormControlLabel
            sx={{ alignItems: 'flex-start', m: 0 }}
            control={(
              <Checkbox
                size="small"
                checked={Boolean(overviewLocation.ai_management_enabled)}
                disabled={aiSaving || Boolean(isDemoUser)}
                onChange={async (ev) => {
                  if (isDemoUser) {
                    onDemoActionAttempt?.();
                    return;
                  }
                  const next = ev.target.checked;
                  setAiSaving(true);
                  try {
                    const row = await patchEvohomeAiManagement(overviewLocation.id, next);
                    setOverviewLocation((prev) => (
                      prev ? { ...prev, ...row, ai_management_enabled: next } : prev
                    ));
                  } catch {
                    setOverviewLocation((prev) => (
                      prev ? { ...prev, ai_management_enabled: !next } : prev
                    ));
                  } finally {
                    setAiSaving(false);
                  }
                }}
                sx={{ color: '#888', '&.Mui-checked': { color: ACCENT }, pt: 0.25 }}
              />
            )}
            label={(
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>
                  {t('settings.evohomeDiscovery.aiManagement.label')}
                </Typography>
                <Typography sx={{ fontSize: 12, color: '#999', lineHeight: 1.4 }}>
                  {t('settings.evohomeDiscovery.aiManagement.hint')}
                </Typography>
              </Box>
            )}
          />
        </Box>
      )}

      {showHeatSupplySettings && overviewLocation && overviewLocation.id === selectedLocationId && (
        <HeatSupplySettings
          location={overviewLocation}
          isDemoUser={isDemoUser}
          onDemoActionAttempt={onDemoActionAttempt}
          onSaved={(row) => {
            setOverviewLocation((prev) => (prev ? { ...prev, ...row, id: prev.id } : row));
          }}
        />
      )}

      {/* Horarios — solo admin (este componente ya es admin-only) */}
      <Box sx={{ mb: 1.5, mt: 0.5 }}>
        <Typography sx={{
          fontSize: 12, fontWeight: 700, color: '#999', mb: 0.5,
          letterSpacing: '0.06em', textTransform: 'uppercase',
        }}>
          {t('climateSystem.schedules.title')}
        </Typography>
        <Typography sx={{ fontSize: 12, color: '#888', mb: 0.35 }}>
          {t('climateSystem.schedules.hint')}
        </Typography>
        <Typography sx={{ fontSize: 12, color: '#888', mb: 1 }}>
          {t('climateSystem.schedules.reorderHint')}
          {reorderBusy ? ` · ${t('climateSystem.schedules.reordering')}` : ''}
        </Typography>
        {zones.length === 0 ? (
          <Typography sx={{ fontSize: 12, color: '#777' }}>
            {t('climateSystem.schedules.emptyZones')}
          </Typography>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={(e) => { void handleZonesDragEnd(e); }}
          >
            <SortableContext items={zones.map((z) => z.id)} strategy={rectSortingStrategy}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: isHorizontal
                    ? `repeat(auto-fill, minmax(min(100%, 340px), 1fr))`
                    : '1fr',
                  gap: isHorizontal ? '10px' : '8px',
                  width: '100%',
                }}
              >
                {zones.map((z) => (
                  <SortableScheduleZoneCard
                    key={z.id}
                    zone={z}
                    schedule={scheduleCache[z.id]}
                    onOpen={() => setScheduleZone(z)}
                  />
                ))}
              </Box>
            </SortableContext>
          </DndContext>
        )}
      </Box>

      {error && (
        <Typography sx={{ color: '#e57373', fontSize: 12, mt: 1 }}>{String(error)}</Typography>
      )}

      <Dialog
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        PaperProps={{
          style: {
            backgroundColor: '#1E1E1E',
            color: 'white',
            minWidth: 'min(400px, calc(100vw - 32px))',
            maxWidth: '560px',
          },
        }}
      >
        <DialogTitle
          sx={{
            color: ACCENT,
            fontSize: '1rem',
            fontWeight: 'bold',
            paddingBottom: 0.75,
            paddingTop: 1.5,
          }}
        >
          {t('settings.evohomeDiscovery.systemHelp.title')}
        </DialogTitle>
        <DialogContent sx={{ paddingTop: '4px', paddingBottom: '4px' }}>
          <Typography sx={{ color: '#ccc', fontSize: '0.78rem', marginBottom: 1, lineHeight: 1.3 }}>
            {t('settings.evohomeDiscovery.systemHelp.intro')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.85 }}>
            {SYSTEM_MODE_HELP_KEYS.map((key) => (
              <Box key={key}>
                <Typography
                  sx={{
                    color: key === 'heatingOff' ? OFF : HEAT,
                    fontSize: '0.78rem',
                    fontWeight: 'bold',
                    marginBottom: 0.1,
                    lineHeight: 1.25,
                  }}
                >
                  {t(`settings.evohomeDiscovery.systemHelp.${key}Title`)}
                </Typography>
                <Typography sx={{ color: '#ccc', fontSize: '0.72rem', lineHeight: 1.28 }}>
                  {t(`settings.evohomeDiscovery.systemHelp.${key}Desc`)}
                </Typography>
              </Box>
            ))}
            <Box>
              <Typography
                sx={{
                  color: ACCENT,
                  fontSize: '0.78rem',
                  fontWeight: 'bold',
                  marginBottom: 0.1,
                  lineHeight: 1.25,
                }}
              >
                {t('settings.evohomeDiscovery.heatSupplyHelp.title')}
              </Typography>
              {(['s1', 's2', 's3', 's4'] as const).map((key) => (
                <Typography key={key} sx={{ color: '#ccc', fontSize: '0.72rem', lineHeight: 1.28, mt: 0.35 }}>
                  {t(`settings.evohomeDiscovery.heatSupplyHelp.${key}`)}
                </Typography>
              ))}
            </Box>
            <Box>
              <Typography
                sx={{
                  color: HEAT,
                  fontSize: '0.78rem',
                  fontWeight: 'bold',
                  marginBottom: 0.1,
                  lineHeight: 1.25,
                }}
              >
                {t('climateSystem.schedules.helpTitle')}
              </Typography>
              <Typography sx={{ color: '#ccc', fontSize: '0.72rem', lineHeight: 1.28 }}>
                {t('climateSystem.schedules.helpDesc')}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: '12px 16px' }}>
          <Button
            onClick={() => setHelpOpen(false)}
            sx={{
              color: ACCENT,
              fontWeight: 'bold',
              '&:hover': { backgroundColor: 'rgba(35, 145, 255, 0.1)' },
            }}
          >
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EvohomeSystemControls;
